import { ref } from 'vue'
import { api } from './api'
import { listQueue, markFailure, removeFromQueue, type QueuedEvent } from './db'

export const syncing = ref(false)
export const pending = ref(0)

interface SyncResponse {
  accepted: string[]
  rejected: { client_event_id: string; reason: string }[]
}

const TERMINAL_REASONS = new Set([
  'too_old',
  'unknown_event_type',
  'missing_client_event_id',
  'invalid_timestamp',
  'invalid_event_shape',
  'task_not_found',
  'missing_task_id',
])

async function refreshPending(): Promise<void> {
  const queue = await listQueue()
  pending.value = queue.length
}

export async function flushQueue(): Promise<void> {
  if (syncing.value) return
  if (!navigator.onLine) {
    await refreshPending()
    return
  }
  syncing.value = true
  try {
    const queue = await listQueue()
    if (queue.length === 0) {
      pending.value = 0
      return
    }
    // Limitar batch a 100 por seguridad (server tope = 200)
    const batch: QueuedEvent[] = queue.slice(0, 100)
    const payload = batch.map((e) => ({
      client_event_id: e.client_event_id,
      event_type: e.event_type,
      occurred_at_local: e.occurred_at_local,
      latitude: e.latitude,
      longitude: e.longitude,
      accuracy: e.accuracy,
      task_id: e.task_id,
      project_id: e.project_id,
      comment: e.comment,
    }))

    const resp = await api.post<SyncResponse>('/api/attendance/sync-batch', { events: payload })
    const data = resp.data

    for (const cid of data.accepted) {
      await removeFromQueue(cid)
    }
    for (const r of data.rejected) {
      // Si la razón es no recuperable, sacar de la cola para evitar bucle infinito
      if (TERMINAL_REASONS.has(r.reason)) {
        await removeFromQueue(r.client_event_id)
      } else {
        await markFailure(r.client_event_id, r.reason)
      }
    }
  } catch (err) {
    // Errores de red → reintentaremos en el siguiente tick
    console.warn('[sync] flush failed', err)
  } finally {
    syncing.value = false
    await refreshPending()
  }
}

let timer: number | null = null

export function startSyncLoop(): void {
  void refreshPending()
  void flushQueue()

  window.addEventListener('online', () => {
    void flushQueue()
  })
  window.addEventListener('focus', () => {
    void flushQueue()
  })

  if (timer === null) {
    // Reintenta cada 30 s mientras la app esté activa.
    timer = window.setInterval(() => {
      if (navigator.onLine) void flushQueue()
    }, 30_000)
  }
}
